import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Mail, RefreshCw } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { ROUTES } from '@utils/constants';
import Layout from '@components/layout/Layout';
import api from '@services/api';

type VerifyStatus = 'processing' | 'success' | 'error' | 'already_verified';

const EmailVerifyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [status, setStatus] = useState<VerifyStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const verificationAttempted = React.useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      const email = searchParams.get('email');

      if (!token || !email) {
        setStatus('error');
        setErrorMessage('Invalid verification link. Please request a new one.');
        return;
      }

      try {
        // Verify the email token
        const response = await api.post('/auth/verify-email', {
          token,
          email: decodeURIComponent(email),
        });

        const data = response.data;

        if (data.success) {
          setStatus('success');

          // Update user data if logged in to reflect email_verified status
          if (user) {
            updateUser({ email_verified: true });
          }
        } else {
          // Check if email is already verified
          if (data.message?.toLowerCase().includes('already verified')) {
            setStatus('already_verified');
          } else {
            setStatus('error');
            setErrorMessage(data.message || 'Email verification failed');
          }
        }
      } catch (err: any) {
        const message = err.response?.data?.detail || err.response?.data?.message || err.message || 'Email verification failed';

        // Check if already verified
        if (message.toLowerCase().includes('already verified')) {
          setStatus('already_verified');
        } else {
          setStatus('error');
          setErrorMessage(message);
        }
      }
    };

    // Only process if not already attempted
    if (!verificationAttempted.current) {
      verificationAttempted.current = true;
      verifyEmail();
    }
  }, [searchParams, user, updateUser]);

  const handleResendVerification = async () => {
    const email = searchParams.get('email');
    if (!email) return;

    setIsResending(true);
    setResendSuccess(false);

    try {
      await api.post('/auth/resend-verification', {
        email: decodeURIComponent(email),
      });
      setResendSuccess(true);
    } catch (err: any) {
      // Still show success to prevent email enumeration
      setResendSuccess(true);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {status === 'processing' && (
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                  <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Verifying your email...
                </h2>
                <p className="mt-2 text-gray-600">
                  Please wait while we confirm your email address.
                </p>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Email Verified!
                </h2>
                <p className="mt-2 text-gray-600">
                  Your email address has been successfully verified. You now have full access to all features.
                </p>
                <div className="mt-6 space-y-3">
                  {user ? (
                    <button
                      onClick={() => navigate(ROUTES.HOME, { replace: true })}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Continue to App
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Sign In
                    </button>
                  )}
                </div>
              </div>
            )}

            {status === 'already_verified' && (
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Mail className="h-8 w-8 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Already Verified
                </h2>
                <p className="mt-2 text-gray-600">
                  Your email address has already been verified. You're all set!
                </p>
                <div className="mt-6 space-y-3">
                  {user ? (
                    <button
                      onClick={() => navigate(ROUTES.HOME, { replace: true })}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Continue to App
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Sign In
                    </button>
                  )}
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <XCircle className="h-8 w-8 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Verification Failed
                </h2>
                <p className="mt-2 text-gray-600">
                  {errorMessage || 'The verification link is invalid or has expired.'}
                </p>

                {resendSuccess ? (
                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-700 text-sm">
                      A new verification email has been sent. Please check your inbox.
                    </p>
                  </div>
                ) : (
                  <div className="mt-6 space-y-3">
                    <button
                      onClick={handleResendVerification}
                      disabled={isResending}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-primary-600 text-sm font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isResending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Resend Verification Email
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Return to Login
                    </button>
                  </div>
                )}

                <p className="mt-4 text-sm text-gray-500">
                  Verification links expire after 24 hours. Request a new one if needed.
                </p>
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Need help?{' '}
              <Link to="/support" className="text-primary-600 hover:text-primary-500">
                Contact Support
              </Link>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EmailVerifyPage;
